import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Button, Card, CardContent, Chip, Collapse,
  IconButton, Stack, Tooltip, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
} from '@mui/material'
import * as XLSX from 'xlsx'
import _ from 'lodash'
import { useForm } from 'react-hook-form'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { apiConstants, get } from '../../common/constants'
import {
  FormProvider, RHFSearchableSelectField, RHFSelectField, RHFTextField,
} from '../../common/hook-form'
import { FieldLabel, FilterField } from '../../common/FilterBar'
import {
  useGetAllActiveMerchantsIdAndNameCachedQuery,
  useGetAllCourierPartnersCachedQuery,
} from '../../redux/commonApiCalls'
import Loader from '../../common/Loader'
import WeightFlowHeader from './components/WeightFlowHeader'
import RowExpansion from './components/RowExpansion'
import { DiscrepancyBadge, StageBadge } from './components/Badges'
import CopyIcon from '../../common/CopyIcon'

// Tooltip copy lifted verbatim from the source mockup so hover text matches.
const COL_TOOLTIPS = {
  clientPushed: 'Declared by client via API/OMS. Often under-reported.',
  proshipPushed: 'Weight Proship pushed to the courier at booking.',
  prozoAllocated: 'Avg of last 3 courier-actual weights per SKU, with packaging adjustment. Click the hyperlink to see full calculation.',
  transit: 'Transit weight charged by courier at processing hub. Received via data feeds.',
  courierBilled: 'Actual weight billed by courier to client.',
  courierBilledSlab: 'Courier billed weight rounded to slab.',
  weightCharged: 'Weight Prozo bills the merchant.',
  slabBilledClient: 'Prozo billed weight rounded to slab.',
  pushRecon: 'Courier billed vs what Proship pushed at booking. Catches leakage the client-declared baseline cannot see (e.g. a freeze changed the pushed weight).',
}

function LegendDot({ color, label }) {
  return (
    <Stack direction='row' spacing={0.5} alignItems='center'>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{label}</Typography>
    </Stack>
  )
}

function HeaderWithInfo({ label, tip, align = 'right' }) {
  return (
    <Stack
      direction='row'
      spacing={0.5}
      alignItems='center'
      justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
    >
      <span>{label}</span>
      <Tooltip title={tip} arrow placement='top'>
        <InfoOutlinedIcon sx={{ fontSize: 13, color: '#9ca3af', cursor: 'help' }} />
      </Tooltip>
    </Stack>
  )
}

const fmtKg = (v, dp = 3) => (v == null || isNaN(v) ? '—' : `${Number(v).toFixed(dp)} kg`)

const ORDER_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'FORWARD SHIPMENT', label: 'Forward' },
  { value: 'REVERSE SHIPMENT', label: 'Reverse' },
]
const DISPATCH_MODE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'AIR', label: 'Air' },
  { value: 'SURFACE', label: 'Surface' },
]
const DISCREPANCY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'NONE', label: 'None' },
  { value: 'LOW', label: 'Low (<15%)' },
  { value: 'MEDIUM', label: 'Medium (15–30%)' },
  { value: 'HIGH', label: 'High (>30%)' },
]

// ─── Excel export helper (SheetJS) ──────────────────────────────────────────
function downloadExcel(rows, filename) {
  const fmtKgStr  = (v) => (v == null || isNaN(Number(v)) ? '' : Number(v).toFixed(3))
  const fmtNum    = (v, dp = 2) => (v == null || isNaN(Number(v)) ? '' : Number(v).toFixed(dp))
  const fmtDate   = (v) => v
    ? new Date(v).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })
    : ''

  const sheetData = rows.map((row) => {
    const w  = row.weights                || {}
    const cs = row.clientDeclaredSnapshot || {}
    const ps = row.proshipPushedSnapshot  || {}
    const pc = row.prozoCalc              || {}
    const skus = (row.products || [])
      .map((p) => `${p.sku}${p.skuName ? ` (${p.skuName})` : ''} ×${p.quantity ?? 1}`)
      .join('; ')

    return {
      // ── Identification ──────────────────────────────────
      'AWB Number':               row.awbNumber             || '',
      'Merchant':                 row.merchantName          || '',
      'Order ID':                 row.orderId               || '',
      'Client Order Ref':         row.clientOrderReference  || '',
      'Shipment Date':            fmtDate(row.shipmentDate),

      // ── Shipment Info ───────────────────────────────────
      // A1: prefer courier account display (Parent_ServiceType) once the ledger API returns it.
      'Courier Partner':          row.courierAccountDisplay || row.courierPartnerName || row.courierPartnerProzoId || '',
      'Order Type':               row.orderType         || '',
      'Dispatch Mode':            row.dispatchMode      || '',
      'Stage':                    row.stage             || '',
      'Total Units':              row.totalUnits        ?? '',
      'SKUs':                     skus,

      // ── Weights (kg) ────────────────────────────────────
      'Client Pushed (kg)':           fmtKgStr(w.clientDeclared),
      'Proship Pushed (kg)':          fmtKgStr(w.proshipPushed),
      'Prozo Predicted (kg)':         fmtKgStr(w.prozoAllocated),
      'Transit Weight (kg)':          fmtKgStr(w.transitWeightKg),
      'Courier Billed Wt (kg)':       fmtKgStr(w.courierBilledWeightKg),
      'Courier Billed Slab (kg)':     fmtKgStr(w.courierBilledSlabKg),
      'Charged to Client (kg)':       fmtKgStr(w.weightChargedToClientKg),
      'Slab Billed to Client (kg)':   fmtKgStr(w.weightSlabBilledToClientKg),

      // ── Discrepancy ─────────────────────────────────────
      'Discrepancy Level':        row.discrepancyLevel || '',
      'Discrepancy (kg)':         fmtKgStr(row.discrepancyKg),
      'Discrepancy (%)':          fmtNum(row.discrepancyPct),

      // ── Push Recon (billed vs Proship-pushed) ───────────
      'Push Recon Level':         row.courierReconDiffLevel || '',
      'Push Recon (kg)':          fmtKgStr(row.courierReconDiffKg),
      'Push Recon (%)':           fmtNum(row.courierReconDiffPct),

      // ── Client Declared Snapshot ────────────────────────
      'CS Length (cm)':           cs.lengthCm           ?? '',
      'CS Breadth (cm)':          cs.breadthCm          ?? '',
      'CS Height (cm)':           cs.heightCm           ?? '',
      'CS Dead Weight (gms)':     cs.deadWeightGms      ?? '',
      'CS Vol Weight (gms)':      cs.volWeightGms       ?? '',
      'CS Applicable Wt (gms)':   cs.applicableWeightGms ?? '',

      // ── Proship Pushed Snapshot ─────────────────────────
      'PS Length (cm)':           ps.lengthCm           ?? '',
      'PS Breadth (cm)':          ps.breadthCm          ?? '',
      'PS Height (cm)':           ps.heightCm           ?? '',
      'PS Dead Weight (gms)':     ps.deadWeightGms      ?? '',
      'PS Vol Weight (gms)':      ps.volWeightGms       ?? '',
      'PS Applicable Wt (gms)':   ps.applicableWeightGms ?? '',

      // ── Prozo Calculation ───────────────────────────────
      'Calc Status':              pc.calcStatus         || '',
      'Calc Final Weight (kg)':   fmtKgStr(pc.finalWeight),
      'Exact Match Used':         pc.exactMatchUsed != null ? String(pc.exactMatchUsed) : '',
      'Packaging Adj (%)':        fmtNum(pc.packagingAdjPct),
      'Packaging Adj Amount':     pc.packagingAdjAmount ?? '',
      'Raw Calc Weight (kg)':     fmtKgStr(pc.rawCalcWeight),

      // ── Flags ───────────────────────────────────────────
      'Weight Freeze Applied':    row.weightFreezeApplied != null ? String(row.weightFreezeApplied) : '',
      'Weight Freeze Ref':        row.weightFreezeRef    || '',
      'Custom DWS Applied':       row.customDwsApplied   != null ? String(row.customDwsApplied) : '',

      // ── MIS ─────────────────────────────────────────────
      'MIS File Tag':             row.misFileTag    || '',
      'MIS Upload Date':          fmtDate(row.misUploadDate),

      // ── Timestamps ──────────────────────────────────────
      'Created At':               fmtDate(row.createdAt),
      'Updated At':               fmtDate(row.updatedAt),
    }
  })

  const ws = XLSX.utils.json_to_sheet(sheetData)

  // Auto-size columns based on header + content lengths
  const colWidths = Object.keys(sheetData[0] || {}).map((key) => {
    const maxLen = Math.max(
      key.length,
      ...sheetData.map((r) => String(r[key] ?? '').length)
    )
    return { wch: Math.min(maxLen + 2, 40) }
  })
  ws['!cols'] = colWidths

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Weight Ledger')
  XLSX.writeFile(wb, filename)
}

// Inline expandable row — clicking the chevron toggles SKU breakdown +
// AWB-level snapshots. Free DataGrid doesn't support detail panels (Pro-only),
// so we render with a vanilla MUI <Table>.
function LedgerRow({ row, expanded, onToggle, onPredictedClick }) {
  const w = row?.weights || {}
  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 36, p: 0.5 }}>
          <IconButton size='small' onClick={onToggle}>
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {row.awbNumber || '—'}
            <CopyIcon text={row.awbNumber} />
          </Box>
          <Typography sx={{ fontSize: 10.5, color: '#6b7280' }}>
            {row.merchantName || ''}
          </Typography>
        </TableCell>
        <TableCell align='right'>{row.totalUnits ?? '—'}</TableCell>
        <TableCell>
          <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
            {(row.products || []).slice(0, 2).map((p, i) => (
              <Chip
                key={`${p.sku}-${i}`}
                size='small'
                label={`${p.sku || '—'} ×${p.quantity ?? 1}`}
                sx={{ height: 20, fontSize: 10.5, bgcolor: '#f3f4f6' }}
              />
            ))}
            {(row.products || []).length > 2 && (
              <Chip
                size='small'
                label={`+${row.products.length - 2}`}
                sx={{ height: 20, fontSize: 10.5, bgcolor: '#e5e7eb' }}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>{row.courierAccountDisplay || row.courierPartnerName || row.courierPartnerProzoId || '—'}</TableCell>
        <TableCell>
          <Stack direction='row' spacing={0.5}>
            <Chip size='small' label={row.orderType?.split(' ')[0] || '—'} sx={{ height: 20, fontSize: 10.5, bgcolor: '#dbeafe', color: '#1e40af' }} />
            <Chip size='small' label={row.dispatchMode || '—'} sx={{ height: 20, fontSize: 10.5, bgcolor: '#dcfce7', color: '#15803d' }} />
          </Stack>
        </TableCell>
        <TableCell align='right'>{fmtKg(w.clientDeclared)}</TableCell>
        <TableCell align='right'>{fmtKg(w.proshipPushed)}</TableCell>
        <TableCell align='right'>
          <Tooltip title='See how weight was calculated'>
            <Box
              onClick={() => onPredictedClick(row)}
              sx={{ cursor: 'pointer', color: '#6366f1', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {fmtKg(w.prozoAllocated)}
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell align='right'>{fmtKg(w.transitWeightKg)}</TableCell>
        <TableCell align='right'>{fmtKg(w.courierBilledWeightKg)}</TableCell>
        <TableCell align='right'>
          <Chip size='small' label={fmtKg(w.courierBilledSlabKg)} sx={{ height: 20, fontSize: 10.5, bgcolor: '#fef3c7', color: '#92400e' }} />
        </TableCell>
        <TableCell align='right'>{fmtKg(w.weightChargedToClientKg)}</TableCell>
        <TableCell align='right'>
          <Chip size='small' label={fmtKg(w.weightSlabBilledToClientKg)} sx={{ height: 20, fontSize: 10.5, bgcolor: '#dcfce7', color: '#15803d' }} />
        </TableCell>
        <TableCell><StageBadge stage={row.stage} /></TableCell>
        <TableCell><DiscrepancyBadge level={row.discrepancyLevel} /></TableCell>
        <TableCell>
          <DiscrepancyBadge level={row.courierReconDiffLevel} />
          {row.courierReconDiffKg != null && (
            <Typography sx={{ fontSize: 10.5, color: '#6b7280', mt: 0.25 }}>
              {`${row.courierReconDiffKg > 0 ? '+' : ''}${Number(row.courierReconDiffKg).toFixed(3)} kg`}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={17} sx={{ p: 0, borderBottom: expanded ? '1px solid #e5e7eb' : 'none' }}>
          <Collapse in={expanded} unmountOnExit timeout='auto'>
            <RowExpansion row={row} onPredictedClick={onPredictedClick} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function LedgerList() {
  const [data, setData] = useState([])
  // No total count: an exact count over the ~3M-row ledger is a ~90s collection scan on
  // the backend, so the API doesn't return one. We fetch limit+1 rows to learn whether a
  // next page exists and paginate open-ended.
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const [filters, setFilters] = useState({})
  const [expandedAwb, setExpandedAwb] = useState(null)
  const [reload, setReload] = useState(0)
  const navigate = useNavigate()
  // Pass the row through location state so the calc page can render
  // immediately without a refetch; deep-linking still falls back to GET.
  const goToCalc = (row) =>
    navigate(`/weight/ledger/calc/${row.awbNumber}`, { state: { row } })

  const methods = useForm({
    defaultValues: {
      awb: '',
      skuId: '',
      merchantId: '',
      courierPartnerProzoId: '',
      orderType: '',
      dispatchMode: '',
      discrepancyLevel: '',
    },
  })
  const { handleSubmit, reset } = methods

  const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const { data: allCouriers = [] } = useGetAllCourierPartnersCachedQuery({
    andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
    offset: 0,
    limit: 1000,
    skipCount: true,
    includes: 'id,name,prozoId',
  })

  // No 'All' sentinel — Autocomplete's built-in × clear button handles
  // resetting the filter, and an empty input means "no filter applied".
  const merchantOptions = useMemo(
    () => _.orderBy(allMerchants, 'name').map((m) => ({ value: m.id, label: m.name })),
    [allMerchants]
  )
  const courierOptions = useMemo(
    () => _.orderBy(allCouriers, 'name').map((c) => ({ value: c.prozoId, label: c.name })),
    [allCouriers]
  )

  // Fetch on filter / pagination change
  useEffect(() => {
    setLoading(true)
    const params = {
      limit: sizePerPage + 1, // one extra row => "is there a next page?"
      skip: page * sizePerPage,
    }
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v
    })
    get(apiConstants.WEIGHT_LEDGER, params)
      .then((res) => {
        const rows = res?.data?.data || []
        setHasMore(rows.length > sizePerPage)
        setData(rows.slice(0, sizePerPage))
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
  }, [page, sizePerPage, filters, reload])

  const onSubmit = (vals) => {
    // SKU chips in the table render as `${sku} ×${qty}`. Users frequently
    // paste the whole chip label; strip the trailing " ×N" (with whitespace
    // before × so legit SKUs like "BOX_X10" / "abcx1" survive untouched).
    const cleanSkuId = (vals.skuId || '')
      .trim()
      .replace(/\s+[×x]\s*\d+\s*$/i, '')
      .trim()
    // AWB takes priority — backend ignores other filters when awbNumber is set.
    setFilters({
      awbNumber: vals.awb?.trim() || '',
      skuId: cleanSkuId,
      merchantId: vals.merchantId,
      courierPartnerProzoId: vals.courierPartnerProzoId,
      orderType: vals.orderType,
      dispatchMode: vals.dispatchMode,
      discrepancyLevel: vals.discrepancyLevel,
    })
    setPage(0)
  }

  const onReset = () => {
    reset({
      awb: '', skuId: '', merchantId: '', courierPartnerProzoId: '',
      orderType: '', dispatchMode: '', discrepancyLevel: '',
    })
    setFilters({})
    setPage(0)
  }

  // Export the currently loaded page data to Excel — no extra API call
  const handleExport = () => {
    const date = new Date().toISOString().slice(0, 10)
    downloadExcel(data, `weight-ledger-${date}.xlsx`)
  }

  const handleToggle = (awb) => setExpandedAwb((cur) => (cur === awb ? null : awb))

  return (
    <>
      {loading && <Loader />}

      {/* Decorative weight-flow header */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Weight Ledger</Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                Correlating shipment weights across Client, Proship, Prozo, and Courier sources
              </Typography>
            </Box>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Tooltip title='Export current page to Excel (.xlsx)' arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={handleExport}
                    disabled={data.length === 0}
                  >
                    <DownloadIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size='small' onClick={() => setReload((n) => n + 1)}>
                <RefreshIcon fontSize='small' />
              </IconButton>
            </Stack>
          </Stack>
          <WeightFlowHeader />
        </CardContent>
      </Card>

      {/* Filter bar */}
      <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

              <FilterField>
                <FieldLabel>AWB Number</FieldLabel>
                <RHFTextField name='awb' label='' placeholder='AWB Number' fullWidth size='small' InputLabelProps={{ shrink: true }} />
              </FilterField>

              <FilterField>
                <FieldLabel>SKU ID</FieldLabel>
                <RHFTextField name='skuId' label='' placeholder='e.g. SKU_GLOW_OIL' fullWidth size='small' InputLabelProps={{ shrink: true }} />
              </FilterField>

              <FilterField>
                <FieldLabel>Merchant</FieldLabel>
                <RHFSearchableSelectField name='merchantId' label='' options={merchantOptions} fullWidth />
              </FilterField>

              <FilterField>
                <FieldLabel>Courier Partner</FieldLabel>
                <RHFSearchableSelectField name='courierPartnerProzoId' label='' options={courierOptions} fullWidth />
              </FilterField>

              <FilterField>
                <FieldLabel>Order Type</FieldLabel>
                <RHFSelectField name='orderType' label='' placeholder='Select Order Type' options={ORDER_TYPE_OPTIONS} width='100%' size='small' displayEmpty />
              </FilterField>

              <FilterField>
                <FieldLabel>Dispatch Mode</FieldLabel>
                <RHFSelectField name='dispatchMode' label='' placeholder='Select Dispatch Mode' options={DISPATCH_MODE_OPTIONS} width='100%' size='small' displayEmpty />
              </FilterField>

              <FilterField>
                <FieldLabel>Discrepancy Level</FieldLabel>
                <RHFSelectField name='discrepancyLevel' label='' placeholder='Select Discrepancy Level' options={DISCREPANCY_OPTIONS} width='100%' size='small' displayEmpty />
              </FilterField>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                <Box component='button' type='submit'
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                  Filter
                </Box>
                <Box component='button' type='button' onClick={onReset}
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                  Reset
                </Box>
              </Box>

            </Box>
          </FormProvider>
        </CardContent>
      </Card>

      {/* Colour-dot legend (no row-count text: the API returns no total) */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='flex-end'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ mb: 1, px: 0.5 }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' rowGap={0.5}>
          <LegendDot color='#0d9488' label='Client' />
          <LegendDot color='#6366f1' label='Prozo' />
          <LegendDot color='#fb923c' label='Transit Charged Wt' />
          <LegendDot color='#ef4444' label='Transit Charged' />
          <Stack direction='row' spacing={0.5} alignItems='center' sx={{ ml: 1.5 }}>
            <OpenInNewIcon sx={{ fontSize: 12, color: '#6366f1' }} />
            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
              Clickable = See how weight was calculated
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Table */}
      <Card>
        {/* Guarantee 10 rows visible before scroll: ~10 rows × ~58px + header ~52px ≈ 640px */}
        <TableContainer sx={{ maxHeight: 'max(calc(100vh - 360px), 640px)' }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow sx={{
                '& th': {
                  fontSize: 11, fontWeight: 700, color: '#374151', bgcolor: '#f5f7fa',
                  textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.2, py: 1,
                },
              }}>
                <TableCell sx={{ width: 36 }} />
                <TableCell>AWB Number</TableCell>
                <TableCell align='right'>Units</TableCell>
                <TableCell>SKUs</TableCell>
                <TableCell>Courier</TableCell>
                <TableCell>Type / Mode</TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Client Pushed (API)' tip={COL_TOOLTIPS.clientPushed} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Proship Pushed' tip={COL_TOOLTIPS.proshipPushed} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Prozo Predicted' tip={COL_TOOLTIPS.prozoAllocated} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Transit Weight' tip={COL_TOOLTIPS.transit} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Courier Billed Wt' tip={COL_TOOLTIPS.courierBilled} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Courier Billed Slab' tip={COL_TOOLTIPS.courierBilledSlab} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Charged to Client' tip={COL_TOOLTIPS.weightCharged} />
                </TableCell>
                <TableCell align='right'>
                  <HeaderWithInfo label='Slab Billed to Client' tip={COL_TOOLTIPS.slabBilledClient} />
                </TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Discrepancy</TableCell>
                <TableCell>
                  <HeaderWithInfo label='Push Recon' tip={COL_TOOLTIPS.pushRecon} align='left' />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={17} align='center' sx={{ py: 4, color: '#9ca3af' }}>
                    No records found.
                  </TableCell>
                </TableRow>
              )}
              {data.map((row) => (
                <LedgerRow
                  key={row.id || row.awbNumber}
                  row={row}
                  expanded={expandedAwb === row.awbNumber}
                  onToggle={() => handleToggle(row.awbNumber)}
                  onPredictedClick={goToCalc}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Open-ended pagination: count={-1} tells MUI the total is unknown; Next is driven
            by hasMore (limit+1 probe) instead. */}
        <TablePagination
          component='div'
          count={-1}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          nextIconButtonProps={{ disabled: loading || !hasMore }}
          labelDisplayedRows={({ from, to }) =>
            data.length === 0 ? '0 rows' : `${from}–${from + data.length - 1}`
          }
          rowsPerPage={sizePerPage}
          onRowsPerPageChange={(e) => {
            setSizePerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Card>

    </>
  )
}
